import { useEffect } from "react";

import { isBrowser } from "~/utils/helpers";
import { logger } from "~/utils/logging";

import { PAGE_SHOULD_SAVE_STATE_EVENT } from "../utils/events";

type PageState<PageData> = {
  data?: PageData;
  scrollY: number;
};

/**
 * Saves page state information so that native mobile apps
 * can restore the page if they are unloaded from memory.
 */
export function usePageState<PageData = void>({
  pageType,
  restore,
  save,
}: {
  pageType: string;
  restore?: (data: PageData) => void;
  save?: () => PageData;
}) {
  // Restore
  useEffect(
    () => {
      if (!isBrowser()) return;
      const key = createLocalStorageKey(pageType);
      const pageStateJSON = window.localStorage.getItem(key);
      window.localStorage.removeItem(key);
      const { didReloadFromMemoryWarning } = window.BibleProjectApp ?? {};
      if (didReloadFromMemoryWarning !== true) return;
      if (!pageStateJSON) return;
      try {
        const pageState = JSON.parse(pageStateJSON) as PageState<PageData>;
        if (restore && pageState.data) {
          restore(pageState.data);
        }
        window.scrollTo(0, pageState.scrollY);
      } catch (error) {
        logger.error("Failed to restore page state", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  // Save
  useEffect(() => {
    if (!isBrowser()) return;
    function shouldSaveHandler() {
      try {
        const pageState: PageState<PageData> = {
          data: save?.(),
          scrollY: window.scrollY,
        };
        window.localStorage.setItem(
          createLocalStorageKey(pageType),
          JSON.stringify(pageState),
        );
      } catch (error) {
        logger.error("Failed to save page state", error);
      }
    }
    document.addEventListener(PAGE_SHOULD_SAVE_STATE_EVENT, shouldSaveHandler);
    return () => {
      document.removeEventListener(
        PAGE_SHOULD_SAVE_STATE_EVENT,
        shouldSaveHandler,
      );
    };
  }, [pageType, save]);
}

function createLocalStorageKey(pageType: string) {
  return `${PAGE_SHOULD_SAVE_STATE_EVENT}:${pageType}`;
}
